import { instance } from './api';
import { APIResponse } from 'tools/types';

export type PipelineType = 'completed' | 'scheduled' | 'error' | 'no run' | 'running' | 'aborted';
export interface Pipeline {
    id: number;
    type: string;
    name: string;
    schedule: string;
    completion_time: string;
    runs: {
        [key: string]: PipelineType;
    }[];
    children: Pipeline[];
}

export interface SystemStatusData {
    hostname: {
        status: string;
    };
    license_info: {
        status: string;
        data: {
            licenseExpiration: string;
            type: string;
            value: string;
        };
    };
    syslog: {
        status: string;
        message: string;
    };
    system_info: {
        status: string;
        data: {
            installed_date: string;
            project_version: string;
            security_build: string | null;
        };
    };
}

export interface LdapConnectivityData {
    ldap_status: {
        status: string;
        disabled: boolean;
        message: string;
        stateless: boolean;
    };
}

export interface MailConnectivityData {
    email_send_receive: {
        status: string;
        message: string;
    };
}

export interface DiskSpaceData {
    volumes: {
        status: string;
        data: {
            label: string;
            used: number;
        }[];
    };
}

interface SqlParameter {
    name: string;
    value: number | string;
    formatData: number | string;
    order: number;
    min?: number;
    max?: number;
    default?: number | string;
    inBytes?: boolean;
    style?: string;
    styleStatus?: 'normal' | 'error' | string;
    qtip?: string;
}

export interface LargeTableInfo {
    table_name: string;
    size_gb: string;
    database_name: string;
}

export interface PkOutOfRangeTableInfo {
    table_name: string;
    percent_full: string;
}

export interface DBStatusData {
    elements_with_error: {
        status: string;
        data: {
            elementsCount: number;
            elementsPercentage: number;
        };
    };
    email_queue: {
        status: string;
        value: string;
        data: {
            count: number;
            default_email: string;
            failed: number;
        };
    };
    mysql_params: {
        status: string;
        data: {
            localhost: boolean;
            sql: SqlParameter[];
        };
    };
    mysql_table_limits: {
        status: string;
        data: {
            large_tables: LargeTableInfo[];
            pk_out_of_range_tables: PkOutOfRangeTableInfo[];
        };
    };
    running_elements: {
        status: string;
        data: {
            auto: number;
            manual: number;
        };
        value: string;
    };
    running_rdps: {
        status: string;
        value: string;
    };
    system_notification: {
        status: string;
        value: string;
        data: {
            by: string;
            date: string;
            status: boolean;
            time: string;
        };
    };
    trigger_crashed: {
        status: string;
        value: string;
        data: any;
    };
    trigger_run: {
        status: string;
        value: string;
        data: any;
    };
}

export const monitorAPI = {
    async getMonitorTime() {
        const response = await instance.get(`/data/monitor/get-time`);
        return response.data;
    },

    async getPipelineTime() {
        const response = await instance.get(`/data/monitor/system-datetime`);
        return response.data?.data;
    },

    async getPipelinesList(): Promise<Pipeline[]> {
        const response = await instance.get(`/data/mock/monitor/pipelines`);
        return response?.data?.data?.list ?? [];
    },
    loadSystemDateTimeData() {
        return instance.get(`data/monitor/system-datetime`);
    },
    toggleSystemNotifications(data: { status: boolean }) {
        return instance.post(`data/monitor/system-notifications`, data);
    },
    getSystemNotificationsState() {
        return instance.get(`data/monitor/system-notifications`);
    },

    async getLdapConnectivity(): Promise<LdapConnectivityData> {
        const response = await instance.get<APIResponse<LdapConnectivityData>>(`data/monitor/ldap-connectivity`);

        return response.data.data;
    },
    async getMailConnectivity(): Promise<MailConnectivityData> {
        const response = await instance.get<APIResponse<MailConnectivityData>>(`data/monitor/mail-connectivity`);

        return response.data.data;
    },
    async getSystemStatus(): Promise<SystemStatusData> {
        const response = await instance.get<APIResponse<SystemStatusData>>(`data/monitor/system-status`);

        return response.data.data;
    },
    async getDiskSpace(): Promise<DiskSpaceData> {
        const response = await instance.get<APIResponse<DiskSpaceData>>(`data/monitor/disk-space`);

        return response.data.data;
    },
    async getDBStatus(): Promise<DBStatusData> {
        const response = await instance.get<APIResponse<DBStatusData>>(`data/monitor/db-status`);

        return response.data.data;
    },
    sendTestEmail(email: string) {
        return instance.post(`data/monitor/emails-queue/sent-test-email`, { send_to: email });
    },
    sendNow() {
        return instance.post(`data/monitor/emails-queue/sent-now-email`);
    },
    clearMailQueue() {
        return instance.post(`data/monitor/emails-queue/clear-email-queue`);
    },
    getLdapStatus() {
        return instance.get(`data/monitor/status-ldap/refresh`);
    },
    getLdapConnections() {
        return instance.get(`data/monitor/status-ldap/get-connections`);
    },
    testLdap(username: string, password: string, server: string) {
        return instance.post(`data/monitor/ldap/test`, { username, password, server });
    },
};
